<template>
  <v-sheet
    class="top-bar d-flex"
    height="58px"
  >
    <div class="w-25" />
    <div class="w-50 d-flex px-4">
      <div class="my-auto">
        <v-btn
          v-if="isTemplate"
          variant="outlined"
          rounded="lg"
          color="primary"
          size="small"
          :disabled="publishDisabled"
          @click="showModal = true"
        >
          Publish
        </v-btn>
        <v-btn
          v-if="!isTemplate && !isHrisProfile"
          variant="outlined"
          rounded="lg"
          color="primary"
          size="small"
          :disabled="applyTemplateDisabled"
          @click="showApplyModal = true"
        >
          Apply Template
        </v-btn>
        <v-btn
          v-if="isHrisProfile && !jobProfile?.draft_profiles?.length"
          variant="outlined"
          rounded="lg"
          color="primary"
          size="small"
          :disabled="modalLoading"
          @click="setModal({ field: 'createDraftFromHris', value: true })"
        >
          Create Draft
        </v-btn>
        <FirstDraftButton
          v-if="!isHrisProfile && !isTemplate"
          :disabled="rescorePending"
          variant="plain"
          class="pa-0 ml-4"
          height="24"
          @click="handleIterateWithFirstDraft"
        >
          <span class="text-button-2">Iterate with First Draft</span>
        </FirstDraftButton>
      </div>
    </div>

    <div class="w-25 d-flex justify-end">
      <TemplateUsageToggle
        v-if="isTemplate"
        class="pr-2 my-auto"
      />
      <HistoryToggle
        class="pr-2 my-auto"
      />
      <CommentsToggle
        class="pr-2 my-auto"
      />
      <v-menu
        v-if="!isHrisProfile"
        location="bottom end"
        transition="scale-transition"
      >
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            class="my-auto mr-2"
            color="textDarkest"
            icon
            size="small"
            variant="text"
          >
            <v-icon icon="$dots-vertical" />
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item
            class="text-body-2 text-textDarkest"
            @click="showDeleteModal = true"
          >
            <v-list-item-title>Delete</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-sheet>

  <Modal
    :show="showModal"
    cancel-text="Cancel"
    action-text="Publish"
    @close="showModal = false"
    @action="handlePublish"
  >
    <template #title>
      Publish Template
    </template>
    <template #content>
      Publishing this template makes its current content available when creating new job profiles.
    </template>
  </Modal>

  <Modal
    :show="showApplyModal"
    cancel-text="Cancel"
    action-text="Apply"
    :disabled="listLoading || !selectedTemplateUlid"
    @close="handleApplyModalClose"
    @action="handleApplyTemplate"
  >
    <template #title>
      Apply Template
    </template>
    <template #content>
      <p class="mb-4">
        Applying a template will replace the current content of this job profile with the selected template's content.
      </p>
      <DpplSelect
        v-model="selectedTemplateUlid"
        :disabled="listLoading"
        :items="templateItems"
        item-title="title"
        item-value="value"
        label="Template"
        variant="outlined"
        placeholder="Select a template"
      />
    </template>
  </Modal>

  <Modal
    :show="showDeleteModal"
    action-text="Delete"
    cancel-text="Cancel"
    :button-props="{ color: 'error' }"
    @close="showDeleteModal = false"
    @action="handleDeleteConfirm"
  >
    <template #title>
      {{ deleteModalTitle }}
    </template>
    <template #content>
      This will permanently delete this {{ isTemplate ? 'template' : 'job profile' }}
      and all of its revisions. This action cannot be undone.
    </template>
  </Modal>

  <Modal
    :show="show.createDraftFromHris"
    cancel-text="Cancel"
    action-text="Create Draft"
    :disabled="modalLoading"
    @close="modals.setModal({ field: 'createDraftFromHris', value: false })"
    @action="handleCreateDraft"
  >
    <template #title>
      Create Draft
    </template>
    <template #content>
      A new draft job profile with the same content will be created.
    </template>
  </Modal>

  <Modal
    :show="showIterateModal"
    cancel-text="Cancel"
    action-text="Iterate"
    @close="showIterateModal = false"
    @action="handleIterateConfirm"
  >
    <template #title>
      Iterate with First Draft
    </template>
    <template #content>
      <p>Generating will overwrite the current content of this job profile.</p>
      <v-checkbox
        v-if="hasSummaryContent"
        v-model="useExistingSummary"
        label="Use existing summary content to generate new content"
        density="compact"
        hide-details
        class="mt-3"
      />
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useModals } from '@/stores/modals/modals';
import Modal from '@/components/shared/Modal.vue';
import DpplSelect from '@/components/datapeople/DpplSelect.vue';
import CommentsToggle from '@/components/document/rightPanel/CommentsToggle.vue';
import HistoryToggle from '@/components/document/rightPanel/HistoryToggle.vue';
import TemplateUsageToggle from '@/components/document/rightPanel/TemplateUsageToggle.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { isEmptyHtml } from '@ui/helpers/htmlToText';

const currentJobProfile = useCurrentJobProfile();
const {
  isTemplate, isHrisProfile, rescorePending, isAlreadyFinalized, jobProfile, summaryField, summaryFieldId,
} = storeToRefs(currentJobProfile);

const { route } = storeToRefs(useAppRoute());
const { finalizedTemplates } = storeToRefs(useJobProfiles());

const { listLoading, modalLoading } = storeToRefs(useAppLoading());
const { show } = storeToRefs(useModals());
const { setModal } = useModals();
const router = useRouter();

const showModal = ref(false);
const showApplyModal = ref(false);
const showDeleteModal = ref(false);
const showIterateModal = ref(false);
const useExistingSummary = ref(true);
const selectedTemplateUlid = ref(null);

const hasSummaryContent = computed(() => {
  if (!summaryField.value?.html) return false;
  return !isEmptyHtml(summaryField.value.html);
});

const deleteModalTitle = computed(() => (
  isTemplate.value ? 'Delete template?' : 'Delete draft job profile?'
));

const publishDisabled = computed(() => (
  rescorePending.value || isAlreadyFinalized.value
));

const applyTemplateDisabled = computed(() => (
  rescorePending.value
));

const templateItems = computed(() => (finalizedTemplates.value || []).map((t) => ({
  title: t.title,
  value: t.ulid,
})));

watch(showApplyModal, (val) => {
  if (val) {
    selectedTemplateUlid.value = null;
    useJobProfiles().loadFinalizedProfileTemplates();
  }
});

const handleIterateWithFirstDraft = () => {
  useExistingSummary.value = true;
  showIterateModal.value = true;
};

const handleIterateConfirm = async () => {
  showIterateModal.value = false;
  await useFirstDraft().generateAndApplyJobProfile({
    ulid: jobProfile.value?.ulid,
    useExistingSummary: useExistingSummary.value,
    summaryFieldId: summaryFieldId.value,
  });
};

const handleCreateDraft = async () => {
  const documentUlid = await currentJobProfile.createDraftFromHrisProfile();
  setModal({ field: 'createDraftFromHris', value: false });
  await router.push({ name: 'job-profile-view', params: { id: documentUlid } });
};

const handlePublish = async () => {
  await currentJobProfile.finalizeTemplate();
  showModal.value = false;
};

const handleApplyModalClose = () => {
  showApplyModal.value = false;
  selectedTemplateUlid.value = null;
};

const handleApplyTemplate = async () => {
  const { documentUlid, oldRevisionUlid } = await currentJobProfile.applyTemplate({
    templateUlid: selectedTemplateUlid.value,
  });
  await currentJobProfile.loadJobProfile({ ulid: documentUlid });

  showApplyModal.value = false;
  selectedTemplateUlid.value = null;

  return router.replace({
    name: 'job-profile-view',
    params: { id: documentUlid },
    query: {
      ...(route.value?.query || {}),
      sideRevision: oldRevisionUlid,
    },
  });
};

const handleDeleteConfirm = async () => {
  const destination = isTemplate.value ? 'job-profile-templates' : 'draft-job-profiles';
  showDeleteModal.value = false;
  await currentJobProfile.deleteJobProfile();
  await router.push({ name: destination });
};
</script>
